import {
    useMemo,
    useCallback,
    isValidElement,
    Children,
    ReactElement,
    ReactNode,
    FC,
    Key,
} from 'react';
import {CSSObject} from '@emotion/react';
import {TopicModule} from '@/services/pages/channel/channel.client';
import useMergeState from '../../hooks/useMergeState';
import TabBar from './tab-bar';
import TabPane from './tab-pane';

interface SimpleTabsProps {
    activeKey?: Key;
    tabBarClassName?: string;
    tabStyle?: CSSObject;
    children?: ReactNode;
    onChange?: (activeKey: string) => void;
    page?: string;
    sfRef?: string;
    infos?: TopicModule;
    channelBusinessLine?: string;
}

const SimpleTabs: FC<SimpleTabsProps> = ({
    activeKey,
    tabBarClassName,
    tabStyle,
    children,
    onChange,
    page = '',
    sfRef = '',
    infos,
    channelBusinessLine = '',
}) => {

    const panes = useMemo(
        () => {
            const result: ReactElement[] = [];
            Children.forEach(children, child => {
                if (!isValidElement(child)) {
                    return;
                }
                result.push(child);
            });

            return result;
        },
        [children]
    );

    const [mergedActiveKey, setMergedActiveKey] = useMergeState<Key | null>(panes[0]?.key, {
        value: activeKey,
        onChange,
    });

    const handleTabChange = useCallback(
        async index => {
            const key = panes[index].key;
            setMergedActiveKey(key);
        },
        [panes, setMergedActiveKey]
    );

    const pane = useMemo(
        () => panes.filter(p => String(mergedActiveKey) === p.key),
        [mergedActiveKey, panes]
    );
    return (
        <>
            <TabBar
                className={tabBarClassName}
                activeKey={mergedActiveKey}
                tabStyle={tabStyle}
                panes={panes}
                onChange={handleTabChange}
                page={page}
                sfRef={sfRef}
                infos={infos}
                channelBusinessLine={channelBusinessLine}
            />
            {pane}
        </>
    );
};

type MixSimpleTabs = typeof SimpleTabs & { TabPane: typeof TabPane };

(SimpleTabs as MixSimpleTabs).TabPane = TabPane;

export default SimpleTabs as MixSimpleTabs;
